import React, { useState, useCallback, useEffect } from 'react';
import { createUseStyles } from 'react-jss';

import Container from 'react-bootstrap/Container';
import Col from 'react-bootstrap/Col';
import clsx from 'clsx';

import Paper from '../components/Paper';
import Text from '../components/Text';
import Details from './ProductDetails/Details';
import Reviews from './ProductDetails/Reviews';
import MoreProducts from './ProductDetails/MoreProducts';
import { Product, Twig } from '../types';
import AddToCartModal from '../components/AddToCartModal';
import { reaction } from '../services/productService';
import { pageTrack, track } from '../services/mixpanelService';
import { setMetaData } from '../services/helpers';
import { Helmet } from 'react-helmet';
import Trends from './TwigDetails/Trends';
import Loader from '../components/Loader';
import { getTwigsByProductID } from '../services/TwigService';
import { Row } from 'react-bootstrap';

export interface ProductDetailsProps {
  product: Product;
}

const ProductDetails: React.FC<ProductDetailsProps> = ({ product }) => {
  const classes = useStyles();
  const [showModal, setShowModal] = useState(false);
  const [loadingRelated, setLoadingRelated] = useState(false);
  const [relatedTwigs, setRelatedTwigs] = useState<Twig[]>([]);


  useEffect(() => {
    (async () => {
      await reaction({ "type": "View", "product_id": product.id })
      track("view_item", { "prodID": product.id })
      pageTrack("product_details_screen")
      setLoadingRelated(true);
      try {
        const { twigs: response, pagination } = await getTwigsByProductID(product.id);
        setRelatedTwigs(response);
      } catch (e) { }
      setLoadingRelated(false);
    })()
  }, []);



  const handleToggleModal = useCallback(() => {
    (async () => {
      setShowModal(!showModal);
      if (!showModal) {
        await reaction({ "type": "Click", "product_id": product.id })
      }
    })()
  }, [showModal]);

  setMetaData({ "title": product.name + ' twigBIG | Social eCommerce Marketplace in Beauty and Fashion', 'image': product.photo, 'description': product.description, "url": window.location.href })
  return (
    <>

      <Helmet>
        <title>{product.name}| twigBIG | Social eCommerce Marketplace in Beauty and Fashion</title>
        <meta property="og:image" content={product.photo} />
        <meta property="og:description" content={<h1>{product.name}</h1> + '| twigBIG | Social eCommerce Marketplace in Beauty and Fashion'} />
        <script type="application/ld+json">
          {
          `{
            "@context": "https://schema.org/",
            "@type": "Product",
            "name": "${product.name}",
            "image": "${product.photo}",
            "description": "${product.description}",
            "brand": "${product.brand?.name}",
            "url":"${`https://twigbig.com/${product.uniqueName}`}",
             
            
           "review": ${relatedTwigs.length ? `[${relatedTwigs.map((twig:Twig)=>`
           {
             "@type": "Review",
             "author": "${twig.user.username}",
             "datePublished": "${twig.createdAt}",
             "reviewBody": "${twig.description}",
             "name": "${twig.uniqueName}",
             "image":"${twig.mediaThumbUrl}",
             "interactionStatistic": [
               {
                 "@type": "InteractionCounter",
                 "interactionService": {
                   "@type": "WebSite",
                   "name": "twigBig",
                   "url": "https://twigbig.com/"
                 },
                 "interactionType": "https://schema.org/LikeAction",
                 "userInteractionCount": "${twig.favorites}"
               },
               {
                 "@type": "InteractionCounter",
                 "interactionService": {
                   "@type": "SoftwareApplication",
                   "name": "TwigBig",
                   "url": "https://play.google.com/store/apps/details?id=com.twigbig.app"
                 },
                 "interactionType": "https://schema.org/LikeAction",
                 "userInteractionCount": "${twig.favorites}"
               }
                ]
                ${relatedTwigs[relatedTwigs.length-1].id===twig.id ? '}]' : '}'}
           `)}
           }` : `[]`}
            
          }`
          }
          </script>
      </Helmet>
      {/* <Container className={clsx(classes.customContainer, 'mt-5')}> */}
      <div className={clsx(classes.customContainer, 'mt-5')}>
        <AddToCartModal
          product={product}
          onClose={handleToggleModal}
          onHide={handleToggleModal}

          show={showModal}
        />
        <Row>
          {/* <Col className={classes.customCol}> */}
          <Col xs={12} lg={loadingRelated || relatedTwigs.length > 0 ? 8 : 12} className={classes.customCol}>
            <Paper withShadow={false} className={classes.detailsContainer}>
              <Details product={product} onAddToCart={handleToggleModal} />
              {/* <div className="mt-5" />
          <Reviews /> */}
            </Paper>
          </Col>

          {loadingRelated || relatedTwigs.length > 0 ? (
            <Col xs={12} lg={4} xl={{ span: 3 }} className="ml-xl-4 p-0">
              <div className={classes.trendsContainer}>
                <Loader className={classes.loader} loading={loadingRelated} center>
                  {() => <Trends trends={relatedTwigs} />}
                </Loader>
              </div>
            </Col>
          ) : null}
          {/* <div className={classes.linksBox}>
          <Text bold>
            <a className={classes.link} href="/">
              More from L'oreal
            </a>
          </Text>
          <Text bold>
            <a className={classes.link} href="/">
              View All
            </a>
          </Text>
        </div>
        <div>
          <MoreProducts />
        </div> */}
        </Row>
      </div>

      {/* </Container> */}
    </>
  );
};

const useStyles = createUseStyles({
  customContainer: {
    margin: "0px 40px"
  },
  customCol: {},
  '@media (max-width: 768px)': {
    customContainer: {
      maxWidth: "100% !important",
      padding: "0px",
      margin: "0px",
      marginTop: "0px!important"
    },
    customCol: {
      padding: "0px"
    },
    detailsContainer: {
      borderRadius: "0px",
      padding: '30px 10px !important',
    }
  },
  detailsContainer: {
    maxWidth: '900px',
    margin: 'auto',
    padding: '30px',
  },
  linksBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '30px',
  },
  link: {
    color: '#555555',
    '&:hover': {
      color: '#555555',
    },
  },
  trendsContainer: {
    position: "sticky",
    top: "0",
    '@media screen and (max-width: 1024px)': {
      marginTop: '40px',
      marginLeft: '15px',
      marginRight: '15px'
    },
  },
  loader: {
    minHeight: '50vh',
  },
});

export default ProductDetails;
